export interface LadderTier {
  code: string;
  name: string;
  multiplier: number;
  thresholdHours: number | null;
}

export interface NormalizedTier extends LadderTier {
  resolvedThreshold: number;
  flex: number;
  isCollapsed?: boolean;
}

const MAX_RENDERED_TIERS = 4;
const DEFAULT_FIRST_THRESHOLD = 8;
const NULL_THRESHOLD_INCREMENT = 2;
const MIN_FLEX = 1.5;

export function normalizeLadder(rawLadder: LadderTier[]): NormalizedTier[] | null {
  // Step 1: Sanitize
  let tiers: (LadderTier & { isCollapsed?: boolean })[] = rawLadder.filter(
    (t) =>
      Number.isFinite(t.multiplier) &&
      (t.thresholdHours == null || Number.isFinite(t.thresholdHours)),
  );

  if (tiers.length < 2) return null;

  // Step 2: Cap at MAX_RENDERED_TIERS
  if (tiers.length > MAX_RENDERED_TIERS) {
    const rendered = tiers.slice(0, MAX_RENDERED_TIERS - 1);
    const collapsed = tiers.slice(MAX_RENDERED_TIERS - 1);
    rendered.push({
      code: 'COLLAPSED',
      name: `+${collapsed.length} more`,
      multiplier: collapsed[collapsed.length - 1].multiplier,
      thresholdHours: collapsed[collapsed.length - 1].thresholdHours,
      isCollapsed: true,
    });
    tiers = rendered;
  }

  // Step 3: Resolve thresholds.
  // For non-last tiers: resolvedThreshold = next tier's thresholdHours (right boundary of this tier).
  // For last tier: use its own thresholdHours (or fallback if null).
  const resolvedThresholds: number[] = [];
  for (let i = 0; i < tiers.length; i++) {
    const isLast = i === tiers.length - 1;
    const next = tiers[i + 1];
    let threshold: number;
    if (!isLast && next.thresholdHours != null && Number.isFinite(next.thresholdHours)) {
      // Non-last: right boundary = next tier's start threshold
      threshold = next.thresholdHours;
    } else if (!isLast) {
      // Non-last but next has null threshold: fallback
      threshold =
        i === 0 ? DEFAULT_FIRST_THRESHOLD : resolvedThresholds[i - 1] + NULL_THRESHOLD_INCREMENT;
    } else {
      // Last tier: use own thresholdHours or fallback
      const own = tiers[i].thresholdHours;
      if (own != null && Number.isFinite(own)) {
        threshold = own;
      } else {
        threshold =
          i === 0 ? DEFAULT_FIRST_THRESHOLD : resolvedThresholds[i - 1] + NULL_THRESHOLD_INCREMENT;
      }
    }
    resolvedThresholds.push(threshold);
  }

  // Step 4: Enforce strictly increasing thresholds.
  // When bumping, use max(prev_resolved, max raw thresholdHours seen) + 1 to handle
  // cases where raw thresholds are out of order (e.g., OT=6 after ST=8 → bump to 9).
  let maxRawSeen = -Infinity;
  for (let i = 0; i < resolvedThresholds.length; i++) {
    const raw = tiers[i].thresholdHours;
    if (raw != null && Number.isFinite(raw)) {
      maxRawSeen = Math.max(maxRawSeen, raw);
    }
    if (i > 0 && resolvedThresholds[i] <= resolvedThresholds[i - 1]) {
      resolvedThresholds[i] = Math.max(resolvedThresholds[i - 1], maxRawSeen) + 1;
    }
  }

  const resolved: NormalizedTier[] = tiers.map((t, i) => ({
    ...t,
    resolvedThreshold: resolvedThresholds[i],
    flex: 0,
    isCollapsed: t.isCollapsed,
  }));

  // Step 5: Compute flex values
  for (let i = 0; i < resolved.length; i++) {
    const delta =
      i === 0
        ? resolved[i].resolvedThreshold
        : resolved[i].resolvedThreshold - resolved[i - 1].resolvedThreshold;
    resolved[i].flex = Math.max(delta, MIN_FLEX);
  }

  // Step 6: Last-tier floor — ensure final bracket is large enough for content.
  // The floor is at least 3 (guarantees ~18% of a 16-unit total) or 30% of the
  // first tier's flex, whichever is larger.
  const lastIdx = resolved.length - 1;
  const lastFloor = Math.max(3, resolved[0].flex * 0.3);
  resolved[lastIdx].flex = Math.max(resolved[lastIdx].flex, lastFloor);

  // Also ensure non-first, non-last tiers have at least 3 flex for readability
  for (let i = 1; i < lastIdx; i++) {
    resolved[i].flex = Math.max(resolved[i].flex, 3);
  }

  return resolved;
}
